import React from 'react';
import { useUnifiedAuth } from './AuthProvider';

// Auth0 JWT Claims Structure (RFC-compliant + Auth0 extensions)
export interface Auth0JwtPayload {
  sub: string;                 // Subject (user ID)
  scope: string;               // Space-separated permissions
  permissions?: string[];      // Array format (Auth0 optional)
  aud: string;                 // Audience (Scarif API identifier)
  iss: string;                 // Issuer (Auth0 domain)
  exp: number;                 // Expiration time
  iat: number;                 // Issued at
  azp?: string;                // Authorized party (client ID)
  gty?: string;                // Grant type
  email?: string;              // User email
  name?: string;               // User name
  idp?: string;                // Identity provider
}

// Enhanced error types for authentication failures
export interface AuthError {
  type: 'auth0' | 'authorization' | 'token_validation' | 'network' | 'unknown';
  code?: string;
  message: string;
  details?: string;
  userMessage: string;  // User-friendly message for display
}

// Auth0 error handling utility
export const parseAuth0Error = (error: any): AuthError => {
  // Handle Auth0 specific errors
  if (error?.error === 'access_denied') {
    const description = error?.error_description || '';
    
    // Handle wrong username/password errors
    if (description.includes('Wrong email or password') || 
        description.includes('wrong credentials') ||
        description.includes('invalid credentials')) {
      return {
        type: 'auth0',
        code: 'invalid_credentials',
        message: description,
        userMessage: 'Invalid email or password. Please check your credentials and try again.'
      };
    }
    
    // Handle user not found errors
    if (description.includes('user does not exist') ||
        description.includes('user not found')) {
      return {
        type: 'auth0',
        code: 'user_not_found',
        message: description,
        userMessage: 'No account found with that email address. Please check your email or contact support.'
      };
    }
    
    // Handle account blocked/locked errors
    if (description.includes('blocked') || 
        description.includes('locked') ||
        description.includes('too many attempts')) {
      return {
        type: 'auth0',
        code: 'account_blocked',
        message: description,
        userMessage: 'Your account has been temporarily blocked due to too many failed login attempts. Please try again later or contact support.'
      };
    }
    
    // Handle admin/permission errors
    if (description.includes('admin') || description.includes('permission')) {
      return {
        type: 'authorization',
        code: 'admin_unauthorized',
        message: description,
        userMessage: 'Your account is not authorized to access the Ping Pong Feedback interface. Please contact your administrator.'
      };
    }
    
    // Generic access denied
    return {
      type: 'auth0',
      code: 'access_denied',
      message: description || 'Access denied',
      userMessage: 'Access to the Ping Pong Feedback interface was denied. Please check your permissions or try logging in again.'
    };
  }

  // Handle network/API errors
  if (error?.message?.includes('Failed to fetch') || error?.message?.includes('Network Error')) {
    return {
      type: 'network',
      message: error.message,
      userMessage: 'Unable to connect to authentication service. Please check your internet connection and try again.'
    };
  }

  // Handle authorization failures from backend
  if (error?.response?.status === 403) {
    const errorDetail = error.response?.data?.detail || '';
    if (errorDetail.includes('admin') || errorDetail.includes('authorized')) {
      return {
        type: 'authorization',
        code: 'admin_forbidden',
        message: errorDetail,
        userMessage: 'You do not have admin permissions to access this system. Please contact support for assistance.'
      };
    }
  }

  // Handle token validation errors
  if (error?.error === 'invalid_token' || error?.message?.includes('token')) {
    return {
      type: 'token_validation',
      code: 'invalid_token',
      message: error.message || error.error_description,
      userMessage: 'Your authentication session has expired. Please log in again.'
    };
  }

  // Handle login_required errors
  if (error?.error === 'login_required') {
    return {
      type: 'auth0',
      code: 'login_required',
      message: error.error_description || 'Login required',
      userMessage: 'Please log in to access the Ping Pong Feedback interface.'
    };
  }

  // Handle consent_required errors
  if (error?.error === 'consent_required') {
    return {
      type: 'auth0',
      code: 'consent_required',
      message: error.error_description || 'Consent required',
      userMessage: 'Additional consent is required to access this application. Please try logging in again.'
    };
  }

  // Handle interaction_required errors
  if (error?.error === 'interaction_required') {
    return {
      type: 'auth0',
      code: 'interaction_required',
      message: error.error_description || 'User interaction required',
      userMessage: 'User interaction is required to complete authentication. Please try logging in again.'
    };
  }

  // Generic error handling
  return {
    type: 'unknown',
    message: error?.message || error?.error_description || 'An unexpected error occurred',
    userMessage: 'An unexpected error occurred during authentication. Please try again or contact support.'
  };
};

// Utility to check URL parameters for Auth0 errors
export const parseUrlAuth0Error = (): AuthError | null => {
  if (typeof window === 'undefined') return null;
  
  const urlParams = new URLSearchParams(window.location.search);
  const error = urlParams.get('error');
  const errorDescription = urlParams.get('error_description');
  
  if (error) {
    return parseAuth0Error({
      error,
      error_description: errorDescription
    });
  }
  
  return null;
};

// Enhanced hook for token acquisition with error handling
export const useAuth0Token = () => {
  const { getAccessTokenSilently } = useUnifiedAuth();

  const getToken = async (): Promise<string> => {
    try {
      return await getAccessTokenSilently();
    } catch (error) {
      const authError = parseAuth0Error(error);
      console.error('Error getting access token:', authError);
      throw authError;
    }
  };

  return getToken;
};

// Hook for enhanced authentication state with error handling
export const useAuthState = () => {
  const {
    isAuthenticated,
    isLoading,
    error: authError,
    user,
    loginWithRedirect,
    logout
  } = useUnifiedAuth();

  const [urlError, setUrlError] = React.useState<AuthError | null>(null);

  // Check for URL parameters on mount and when URL changes
  React.useEffect(() => {
    const urlAuthError = parseUrlAuth0Error();
    if (urlAuthError) {
      setUrlError(urlAuthError);

      // Clean up URL parameters after capturing the error
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        url.searchParams.delete('error');
        url.searchParams.delete('error_description');
        window.history.replaceState({}, '', url.toString());
      }
    }
  }, []);

  const handleLogin = async (options?: any) => {
    try {
      // Clear any existing errors when starting a new login
      setUrlError(null);
      // Unified context handles provider-specific params (audience, scopes)
      await loginWithRedirect(options);
    } catch (error) {
      const parsedError = parseAuth0Error(error);
      console.error('Login failed:', parsedError);
      throw parsedError;
    }
  };

  const handleLogout = () => {
    setUrlError(null); // Clear errors on logout
    logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  };

  // Parse error if present, prioritizing URL errors over provider errors
  const parsedError = urlError || (authError ? parseAuth0Error(authError) : null);

  return {
    isAuthenticated,
    isLoading,
    user,
    error: parsedError,
    login: handleLogin,
    logout: handleLogout,
    clearError: () => setUrlError(null)
  };
};

// Utility to check if user has admin permissions (Ping-prefixed scopes)
export const validateAdminAccess = (user: any): boolean => {
  // Check for admin permissions in various places (Ping-prefixed scopes)
  const hasAdminScope = user?.scope?.includes('admin:ping_system') || user?.scope?.includes('read:ping_feedback') || user?.scope?.includes('write:ping_feedback');
  const hasAdminPermission = user?.permissions?.includes('admin:ping_system') || user?.permissions?.includes('read:ping_feedback') || user?.permissions?.includes('write:ping_feedback');
  
  // For development, we can be more lenient
  const isDevelopment = import.meta.env.VITE_ENVIRONMENT === 'development';
  
  if (isDevelopment && !hasAdminScope && !hasAdminPermission) {
    console.warn('Development mode: User missing admin permissions but allowing access');
    return true;
  }
  
  return hasAdminScope || hasAdminPermission;
};

// Utility to get user permissions from token
export const getUserPermissions = (user: any): string[] => {
  const permissions: string[] = [];
  
  // Extract from scope (space-separated)
  if (user?.scope) {
    permissions.push(...user.scope.split(' ').filter((s: string) => s.trim()));
  }
  
  // Extract from permissions array (Auth0 optional format)
  if (user?.permissions && Array.isArray(user.permissions)) {
    permissions.push(...user.permissions);
  }
  
  // Remove duplicates and return
  return [...new Set(permissions)];
}; 